import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordResetPages";
import BillingPage from "@/pages/BillingPage";
import LandingPage from "@/pages/LandingPage";
import AdminPage from "@/pages/AdminPage";
import SimulatorPage from "@/pages/SimulatorPage";
import ReportsPage from "@/pages/ReportsPage";
import QuotePublicPage from "@/pages/QuotePublicPage";
import { JoinPage } from "@/components/Team";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import AppLayout from "@/components/AppLayout";
import { AuthCallback } from "@/components/GoogleAuth";
import Dashboard from "@/pages/Dashboard";
import {
  FixedCostsPage, VariableCostsPage, MaterialsPage, EmployeesPage, ProfessionalsPage,
} from "@/pages/CrudPages";
import ProceduresPage from "@/pages/ProceduresPage";
import { QuotesListPage, QuoteNewPage, QuoteViewPage } from "@/pages/QuotesPages";
import SettingsPage from "@/pages/SettingsPage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
          <Route path="/reset-password" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
          <Route path="/app" element={<Protected><AppLayout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="fixed-costs" element={<FixedCostsPage />} />
            <Route path="variable-costs" element={<VariableCostsPage />} />
            <Route path="materials" element={<MaterialsPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="professionals" element={<ProfessionalsPage />} />
            <Route path="procedures" element={<ProceduresPage />} />
            <Route path="quotes" element={<QuotesListPage />} />
            <Route path="quotes/new" element={<QuoteNewPage />} />
            <Route path="quotes/:id" element={<QuoteViewPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="simulator" element={<SimulatorPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="admin" element={<AdminPage />} />
          </Route>
          <Route path="/q/:token" element={<QuotePublicPage />} />
          <Route path="/join/:token" element={<JoinPage />} />
          <Route path="/payment/success" element={<Protected><PaymentSuccessPage /></Protected>} />
          <Route path="/payment/cancel" element={<Protected><PaymentCancelPage /></Protected>} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
